
const PI2 = Math.PI * 2
const TWO_OVER_SQRT_2 = 2.0 / Math.sqrt(2)
const FILL = true

export const circle = (ctx: any, x: number, y: number, r: number) => {
    ctx.arc(x, y, r, 0, PI2, false);
}

export const line = (ctx: any, ax: number, ay: number, bx: number, by: number) => {
    ctx.moveTo(ax, ay);
    ctx.lineTo(bx, by);
}

const WIND_PATHS = [
	[
		-0.7500, -0.1800, -0.7219, -0.1527, -0.6971, -0.1225,
		-0.6739, -0.0910, -0.6516, -0.0588, -0.6298, -0.0262,
		-0.6083,  0.0065, -0.5868,  0.0396, -0.5643,  0.0731,
		-0.5372,  0.1041, -0.5033,  0.1259, -0.4662,  0.1406,
		-0.4275,  0.1493, -0.3881,  0.1530, -0.3487,  0.1526,
		-0.3095,  0.1488, -0.2708,  0.1421, -0.2319,  0.1342,
		-0.1943,  0.1217, -0.1600,  0.1025, -0.1290,  0.0785,
		-0.1012,  0.0509, -0.0764,  0.0206, -0.0547, -0.0120,
		-0.0378, -0.0472, -0.0324, -0.0857, -0.0389, -0.1241,
		-0.0546, -0.1599, -0.0814, -0.1876, -0.1193, -0.1964,
		-0.1582, -0.1935, -0.1931, -0.1769, -0.2157, -0.1453,
		-0.2290, -0.1085, -0.2327, -0.0697, -0.2240, -0.0317,
		-0.2064,  0.0033, -0.1853,  0.0362, -0.1613,  0.0672,
		-0.1350,  0.0961, -0.1051,  0.1213, -0.0706,  0.1397,
		-0.0332,  0.1512,  0.0053,  0.1580,  0.0442,  0.1624,
		0.0833,  0.1636,  0.1224,  0.1615,  0.1613,  0.1565,
		0.1999,  0.1500,  0.2378,  0.1402,  0.2749,  0.1279,
		0.3118,  0.1147,  0.3487,  0.1015,  0.3858,  0.0892,
		0.4236,  0.0787,  0.4621,  0.0715,  0.5012,  0.0702,
		0.5398,  0.0766,  0.5768,  0.0890,  0.6123,  0.1055,
		0.6466,  0.1244,  0.6805,  0.1440,  0.7147,  0.1630,
		0.7500,  0.1800
	],[
		-0.7500,  0.0000, -0.7033,  0.0195, -0.6569,  0.0399,
		-0.6104,  0.0600, -0.5634,  0.0789, -0.5155,  0.0954,
		-0.4667,  0.1089, -0.4174,  0.1206, -0.3676,  0.1299,
		-0.3174,  0.1365, -0.2669,  0.1398, -0.2162,  0.1391,
		-0.1658,  0.1347, -0.1157,  0.1271, -0.0661,  0.1169,
		-0.0170,  0.1046,  0.0316,  0.0903,  0.0791,  0.0728,
		0.1259,  0.0534,  0.1723,  0.0331,  0.2188,  0.0129,
		0.2656, -0.0064,  0.3122, -0.0263,  0.3586, -0.0466,
		0.4052, -0.0665,  0.4525, -0.0847,  0.5007, -0.1002,
		0.5497, -0.1130,  0.5991, -0.1240,  0.6491, -0.1325,
		0.6994, -0.1380,  0.7500, -0.1400
	]
]

const WIND_OFFSETS = [
	{start: 0.36, end: 0.11},
	{start: 0.56, end: 0.16}
]

export type DrawOpts = {
	cx: number
	cy: number
	w: number,
	s: number
}

export const weather = {
	puff: (ctx: any, t: number, cx: number, cy: number, rx: number, ry: number, rmin: number, rmax: number) => {
		const c = Math.cos(t * PI2)
        const s = Math.sin(t * PI2)

		rmax -= rmin

		circle(
			ctx,
			cx - s * rx,
			cy + c * ry + rmax * 0.5,
			rmin + (1 - c * 0.5) * rmax
		)
	},
	puffs: (ctx: any, count: number, t: number, cx: number, cy: number, rx: number, ry: number, rmin: number, rmax: number) => {
		for (let i: number = count; i > 0; i -= 1) {
			ctx.beginPath()
			weather.puff(ctx, t + i / count, cx, cy, rx, ry, rmin, rmax)
			ctx.fill()
		}
	},
	cloud: (ctx: any, t: number, opts: DrawOpts, color: string) => {
		t /= 30000

		const { cx, cy, w: cw, s } = opts
		const a = cw * 0.21
		const b = cw * 0.12
		const c = cw * 0.24
		const d = cw * 0.22

		ctx.fillStyle = color;
		weather.puffs(ctx, 5, t, cx, cy, a, b, c, d);

		if (!FILL) {
			ctx.globalCompositeOperation = 'destination-out';
			weather.puffs(ctx, 5, t, cx, cy, a, b, c - s, d - s);
			ctx.globalCompositeOperation = 'source-over';
		}
	},
	sun: (ctx: any, t: number, opts: DrawOpts, color: string, rayColor: string = null) => {
		t /= 80000;

		const { cx, cy, w: cw, s } = opts
    	const a = cw * 0.25 - s * 0.5
        const b = cw * 0.32 + s * 0.5
		const c = cw * 0.50 - s * 0.5

		ctx.strokeStyle = color;
		ctx.lineWidth = s;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";

		ctx.beginPath()
		ctx.arc(cx, cy, a, 0, PI2, false);
		ctx.stroke();

		if (FILL) {
			ctx.fillStyle = color
			ctx.fill()
		}

		let p: number
		let cos: number
		let sin: number

		rayColor = rayColor || color
		ctx.fillStyle = rayColor
		ctx.strokeStyle = rayColor
		ctx.beginPath()

		for (let i = 8; i > 0; i -= 1) {
			p = (t + i / 8) * PI2;
			cos = Math.cos(p);
			sin = Math.sin(p);
			line(ctx, cx + cos * b, cy + sin * b, cx + cos * c, cy + sin * c);
		}

		ctx.stroke();
	},
	moon: (ctx: any, t: number, opts: DrawOpts, color: string) => {
		t /= 15000;

		let { cx } = opts
		const { cy, w: cw, s } = opts
    	const a = cw * 0.29 - s * 0.5
        const b = cw * 0.05
        const c = Math.cos(t * PI2)
        const p = c * PI2 / -16

		ctx.strokeStyle = color;
		ctx.lineWidth = s;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";

		cx += c * b;

		ctx.beginPath();
		ctx.arc(cx, cy, a, p + PI2 / 8, p + PI2 * 7 / 8, false);
		ctx.arc(cx + Math.cos(p) * a * TWO_OVER_SQRT_2, cy + Math.sin(p) * a * TWO_OVER_SQRT_2, a, p + PI2 * 5 / 8, p + PI2 * 3 / 8, true);
		ctx.stroke();

		if (FILL) {
			ctx.fillStyle = color
			ctx.fill()
		}
	},
	rain: (ctx: any, t: number, opts: DrawOpts, total: number, color: string) => {
		t /= 1050;

		const { cx, cy, w: cw, s } = opts
		const max = 16
		// const a = cw * 0.16
		// const a = (cw * (total / max)) / total
		const a = (cw * 0.5) / 2
        const b = PI2 * 11 / 12
        const c = PI2 *  7 / 12

		ctx.fillStyle = color;

		let p: number
		let x: number
		let y: number
		let factor: number
		let idx: number
		let m: number
		const mid = total / 2

		const interval = (i: number): number => {
			let idx = (i % 2) ? i + mid : i
			if (idx > total) {
				idx -= total
			}
			return idx
		}

		ctx.beginPath();

		for (let i = total; i > 0; i -= 1) {
			idx = interval(i)
			factor = (idx - mid) / mid
			// m = (i % 2) === 0 ? -1 : 1
			p = (t + i / total) % 1
			// x = cx + ((i - 1.5) / 1.5) * (i <= mid ? -1 : 1) * a
			x = cx + factor * a
			y = cy + p * p * cw
			ctx.moveTo(x, y - s * 1.5);
			ctx.arc(x, y, s * 0.75, b, c, false);
		}

		ctx.fill();
	},
	sleet: (ctx: any, t: number, opts: DrawOpts, color: string) => {
		t /= 750;

		const { cx, cy, w: cw, s } = opts
		const a = cw * 0.1875
		const b = PI2 * 11 / 12
		const c = PI2 *  7 / 12

		ctx.strokeStyle = color;
		ctx.lineWidth = s * 0.5;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";

		let p: number
		let x: number
		let y: number

		ctx.beginPath();

		for (let i = 4; i > 0; i -= 1) {
			p = (t + i / 4) % 1;
			x = Math.floor(cx + ((i - 1.5) / 1.5) * (i === 1 || i === 2 ? -1 : 1) * a) + 0.5;
			y = cy + p * cw;
			line(ctx, x, y - s * 1.5, x, y + s * 1.5);
		}

		ctx.stroke();
	},
	snow: (ctx: any, t: number, opts: DrawOpts, total: number, color: string) => {
		t /= 3000;

		const { cx, cy, w: cw, s } = opts
    	const a  = cw * 0.16
        const b  = s * 0.75
        const u  = t * PI2 * 0.7
        const ux = Math.cos(u) * b
        const uy = Math.sin(u) * b
        const v  = u + PI2 / 3
        const vx = Math.cos(v) * b
        const vy = Math.sin(v) * b
        const w  = u + PI2 * 2 / 3
        const wx = Math.cos(w) * b
        const wy = Math.sin(w) * b

		ctx.strokeStyle = color;
		ctx.lineWidth = s * 0.5;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";

		let p: number
		let x: number
		let y: number
		let m: number

		ctx.beginPath();

		for (let i = total; i > 0; i -= 1) {
			m = (i % 2) === 0 ? -1 : 1
			p = (t + i / total) % 1;
			x = cx + Math.sin((p + i / total) * PI2) * a * m
			y = cy + p * cw;

			line(ctx, x - ux, y - uy, x + ux, y + uy);
			line(ctx, x - vx, y - vy, x + vx, y + vy);
			line(ctx, x - wx, y - wy, x + wx, y + wy);
		}

		ctx.stroke();
	},
	fogbank: (ctx: any, t: number, opts: DrawOpts, color: string) => {
		t /= 30000;

		const { cx, cy, w: cw, s } = opts
    	const a = cw * 0.21
        const b = cw * 0.06
        const c = cw * 0.21
        const d = cw * 0.28

		ctx.fillStyle = color;
		weather.puffs(ctx, 5, t, cx, cy, a, b, c, d);

		if (!FILL) {
			ctx.globalCompositeOperation = 'destination-out';
			weather.puffs(ctx, 5, t, cx, cy, a, b, c - s, d - s);
			ctx.globalCompositeOperation = 'source-over';
		}

		ctx.fill();
	},
	foglines: (ctx: any, t: number, opts: DrawOpts, color: string) => {
		t /= 5000;

		const { cx: cw, cy: ch, w: s, s: k } = opts
		const a = Math.cos((t) * PI2) * s * 0.02
		const b = Math.cos((t + 0.25) * PI2) * s * 0.02
		const c = Math.cos((t + 0.50) * PI2) * s * 0.02
		const d = Math.cos((t + 0.75) * PI2) * s * 0.02
		const n = ch * 0.936
		const e = Math.floor(n - k * 0.5) + 0.5
		const f = Math.floor(n - k * 2.5) + 0.5

		ctx.strokeStyle = color;
		ctx.lineWidth = k;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";

		ctx.beginPath();

		line(ctx, a + cw * 0.2 + k * 0.5, e, b + cw * 0.8 - k * 0.5, e);
		line(ctx, c + cw * 0.2 + k * 0.5, f, d + cw * 0.8 - k * 0.5, f);

		ctx.stroke();
	},
	leaf: (ctx: any, t: number, opts: DrawOpts, color: string) => {
		const { cx: x, cy: y, w: cw, s } = opts
		const a = cw / 8
        const b = a / 3
        const c = 2 * b
        const d = (t % 1) * PI2
        const e = Math.cos(d)
        const f = Math.sin(d)

		ctx.fillStyle = color;
		ctx.strokeStyle = color;
		ctx.lineWidth = s;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";

		ctx.beginPath();
		ctx.arc(x, y, a, d, d + Math.PI, false);
		ctx.arc(x - b * e, y - b * f, c, d + Math.PI, d, false);
		ctx.arc(x + c * e, y + c * f, b, d + Math.PI, d, true );

		if (FILL) {
			ctx.fill();
		} else {
			ctx.globalCompositeOperation = 'destination-out';
			ctx.fill();
			ctx.globalCompositeOperation = 'source-over';
			ctx.stroke();
		}
	},
	swoosh: (ctx: any, t: number, opts: DrawOpts, index: number, total: number, color: string, leafColor: string) => {
		t /= 2000;

		const { cx, cy, w: cw, s } = opts
		const path = WIND_PATHS[index]
		let a = (t + index - WIND_OFFSETS[index].start) % total
		let c = (t + index - WIND_OFFSETS[index].end) % total
		let e = (t + index) % total
		let b: number
		let d: number
		let f: number
		let i: number

		ctx.strokeStyle = color;
		ctx.lineWidth = s;
		ctx.lineCap = "round";
		ctx.lineJoin = "round";

		if (a < 1) {
			ctx.beginPath();

			a *= path.length / 2 - 1;
			b = Math.floor(a);
			a -= b;
			b *= 2;
			b += 2;

			ctx.moveTo(
				cx + (path[b - 2] * (1 - a) + path[b] * a) * cw,
				cy + (path[b - 1] * (1 - a) + path[b + 1] * a) * cw
			);

			if (c < 1) {
				c *= path.length / 2 - 1;
				d = Math.floor(c);
				c -= d;
				d *= 2;
				d += 2;

				for (i = b; i !== d; i += 2) {
					ctx.lineTo(cx + path[i] * cw, cy + path[i + 1] * cw);
				}

				ctx.lineTo(
					cx + (path[d - 2] * (1 - c) + path[d] * c) * cw,
					cy + (path[d - 1] * (1 - c) + path[d + 1] * c) * cw
				);
			} else {
				for (i = b; i !== path.length; i += 2) {
					ctx.lineTo(cx + path[i] * cw, cy + path[i + 1] * cw);
				}
			}
			ctx.stroke();
		} else if (c < 1) {
			ctx.beginPath();

			c *= path.length / 2 - 1;
			d = Math.floor(c);
			c -= d;
			d *= 2;
			d += 2;

			ctx.moveTo(cx + path[0] * cw, cy + path[1] * cw);

			for (i = 2; i !== d; i += 2) {
				ctx.lineTo(cx + path[i] * cw, cy + path[i + 1] * cw);
			}

			ctx.lineTo(
				cx + (path[d - 2] * (1 - c) + path[d] * c) * cw,
				cy + (path[d - 1] * (1 - c) + path[d + 1] * c) * cw
			);
			ctx.stroke();
		}

		if (e < 1) {
			e *= path.length / 2 - 1;
			f = Math.floor(e);
			e -= f;
			f *= 2;
			f += 2;

			weather.leaf(ctx, t, {
				cx: cx + (path[f - 2] * (1 - e) + path[f] * e) * cw,
				cy: cy + (path[f - 1] * (1 - e) + path[f + 1] * e) * cw,
				w: cw,
				s
			}, leafColor);
		}
	},
	lightning: (ctx: any, t: number, opts: DrawOpts, color: string) => {
		t /= 1000;

		const { cx, cy, w: cw, s } = opts
    	const alpha = 1 - t % 1
        const a = cw / 25
        const b = Math.floor(t % 4)
        const x = cx + ((b - 1.5) / 1.5) * (b === 1 || b === 2 ? -1 : 1) * cw * 0.16
        const y = cy

		ctx.fillStyle = color;
		ctx.globalAlpha = alpha;

		ctx.beginPath();
		ctx.moveTo(x - a, y - a * 5);
		ctx.lineTo(x - a * 3, y);
		ctx.lineTo(x, y);
		ctx.lineTo(x - a * 2, y + a * 5);
		ctx.lineTo(x + a * 3, y - a);
		ctx.lineTo(x, y - a);
		ctx.lineTo(x + a * 2, y - a * 5);
		ctx.closePath();
		ctx.fill();

		ctx.globalAlpha = 1;
	},
	hail: (ctx: any, t: number, opts: DrawOpts, color: string) => {
		t /= 900;

		const { cx, cy, w: cw, s } = opts
		const a = cw * 0.16
		let p: number
		let x: number
		let y: number

		ctx.fillStyle = color;

		ctx.beginPath();

		for (let i = 8; i > 0; i -= 1) {
			p = (t + i / 8) % 1;
			x = cx + ((i - 3) / 3) * (i === 1 || i === 3 || i === 4 || i === 6 ? -1 : 1) * a;
			y = cy + p * p * cw;
			ctx.moveTo(x, y);
			ctx.arc(x, y, s * 0.4, 0, 2 * Math.PI, false);
		}

		ctx.fill();
	}
}
